import { IOccasionGeniusResponse } from '../interfaces/occasion-genius-response.interface';
import { InitializeField } from './initialize-field.model';
import { OccasionGeniusEvent } from './occasion-genius-event.model';
import {
  IOccasionGeniusEvent,
  IOccasionGeniusRecurringEvent,
} from '../interfaces/occasion-genius-event.interface';

export class OccasionGeniusResponse extends InitializeField<IOccasionGeniusResponse> {
  success: boolean;
  events: {
    non_recurring_events: IOccasionGeniusEvent[];
    recurring_events: IOccasionGeniusRecurringEvent[];
  } = {
    non_recurring_events: [],
    recurring_events: [],
  };

  eventsSearchedAndSorted: Array<
    OccasionGeniusEvent | IOccasionGeniusRecurringEvent
  > = [];
  eventsSearchedAndSortedCloned: Array<
    OccasionGeniusEvent | IOccasionGeniusRecurringEvent
  > = [];
  searchInput: string = '';
  scoreFilters: string[] = [];

  scoreFiltersOptions = [
    { label: '1-25', value: '0' },
    { label: '26-99', value: '1' },
    { label: '100-999', value: '2' },
    { label: '1,000-9,999', value: '3' },
    { label: '10,000-99,000', value: '4' },
    { label: '100,000-999,999', value: '5' },
    { label: '1,000,000+', value: '6' },
  ];

  categoriesOptions: { label: string; value: string }[] = [];

  categoriesFilters: Record<string, { flags: string[]; state: boolean }> = {};
  protected excludedKeys: string[];

  constructor(data: Partial<IOccasionGeniusResponse> = {}) {
    super();
    this.initializeFields(data);
    if (data?.events) {
      this.updateEvents(data?.events);
    }
  }

  updateEvents(events: any): void {
    this.scoreFilters = [];
    this.events = events;
    this.eventsSearchedAndSortedCloned = this.eventsSearchedAndSorted =
      this.mapToModelArray(
        [
          ...(events?.recurring_events || []),
          ...(events?.non_recurring_events || []),
        ],
        OccasionGeniusEvent,
      );

    this.categoriesFilters = this.groupEventsByFlags(
      this.eventsSearchedAndSortedCloned,
    );

    this.categoriesOptions = [];
    Object.keys(this.categoriesFilters).filter((key) => {
      this.categoriesOptions.push({ label: key, value: key });
    });

    this.filterAndSortEvents({ searchInput: '', sortDir: 'asc' });
  }

  protected getDefaultValue(key: string): any {
    switch (key) {
      case 'success':
        return false;
      default:
        return null;
    }
  }

  groupEventsByCategories(): Record<
    string,
    Array<OccasionGeniusEvent | IOccasionGeniusRecurringEvent>
  > {
    const groupedEvents: Record<
      string,
      Array<OccasionGeniusEvent | IOccasionGeniusRecurringEvent>
    > = {};

    if (this.categoriesFilters !== null) {
      Object.keys(this.categoriesFilters).forEach((category) => {
        const categoryResult = this.eventsSearchedAndSorted?.filter((event) => {
          const eventFlags = event.flags.split('"').join('').split(',');
          return this.categoriesFilters[category].flags.some((flag) =>
            eventFlags.includes(flag),
          );
        });

        if (categoryResult.length) {
          groupedEvents[category] = categoryResult;
        }
      });
    }

    return groupedEvents;
  }

  filterAndSortEvents(value: { searchInput: string; sortDir: string }): void {
    const { searchInput, sortDir } = value;
    const scoreFilters: string[] = this.scoreFilters;

    if (searchInput !== this.searchInput) {
      this.searchInput = searchInput;
    }

    const activeCategories = Object.entries(this.categoriesFilters)
      .filter(([_, data]) => data.state)
      .flatMap(([_, data]) => data.flags);

    const filteredEvents = this.eventsSearchedAndSortedCloned.filter(
      (event) => {
        const eventFlags = event.flags.split('"').join('').split(',');

        const matchesCategory =
          activeCategories.length === 0 ||
          activeCategories.some((flag) => eventFlags.includes(flag));
        const matchesSearch =
          !this.searchInput ||
          (event.events.length ? event.events[0] : event).name
            .toLowerCase()
            .includes(this.searchInput.toLowerCase()) ||
          (event.events.length ? event.events[0] : event).description
            ?.toLowerCase()
            .includes(this.searchInput.toLowerCase()) ||
          (event.events.length ? event.events[0] : event).flags.includes(
            this.searchInput.toLowerCase(),
          ) ||
          (event.events.length ? event.events[0] : event).address
            ?.toLowerCase()
            .includes(this.searchInput.toLowerCase());
        const matchesScore =
          scoreFilters.length === 0 ||
          scoreFilters.includes(
            (event.events.length ? event.events[0] : event).popularity_score,
          );

        return matchesCategory && matchesSearch && matchesScore;
      },
    );

    this.eventsSearchedAndSorted = filteredEvents.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      if (nameA < nameB) return sortDir === 'asc' ? -1 : 1;
      if (nameA > nameB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  groupEventsByFlags(
    events: Array<IOccasionGeniusEvent | IOccasionGeniusRecurringEvent>,
  ): Record<string, { flags: string[]; state: boolean }> {
    const filterMapping = {
      'Exhibits and Classes': ['class_workshop', 'museum_exhibit', 'tour'],
      'Sports and Competitions': ['competition'],
      'Live Music and Shows': ['concert', 'performance_show'],
      'Festivals and Expos': ['convention_expo', 'festival', 'parade'],
      Shopping: ['shopping'],
      Social: ['social'],
    };

    const uniqueFilters = {};

    events.forEach((event) => {
      const eventFlags = event.flags.split('"').join('').split(',');

      for (const [filter, flags] of Object.entries(filterMapping)) {
        const matchingFlags = eventFlags.filter((flag) => flags.includes(flag));
        if (matchingFlags.length > 0) {
          if (!uniqueFilters[filter]) {
            uniqueFilters[filter] = { flags: new Set(), state: true };
          }
          matchingFlags.forEach((flag) =>
            uniqueFilters[filter].flags.add(flag),
          );
        }
      }
    });

    const result = {};
    for (const [filter, data] of Object.entries(uniqueFilters)) {
      result[filter] = {
        flags: Array.from((data as any).flags),
        state: (data as any).state,
      };
    }
    return result;
  }

  updateCategoryFilterState(categoryName: string, newState: boolean): void {
    if (this.categoriesFilters[categoryName]) {
      this.categoriesFilters[categoryName].state = newState;
      this.filterAndSortEvents({
        searchInput: this.searchInput,
        sortDir: 'asc',
      });
    }
  }

  updateOneCategoryFilterState(categoryName: string): void {
    Object.keys(this.categoriesFilters).forEach((key) => {
      this.categoriesFilters[key].state = key === categoryName;
    });
    this.filterAndSortEvents({ searchInput: this.searchInput, sortDir: 'asc' });
  }

  updateScoreFilters(newScoreFilters: string[]): void {
    this.scoreFilters = newScoreFilters;
    this.filterAndSortEvents({ searchInput: this.searchInput, sortDir: 'asc' });
  }
}
